import { useState, type FormEvent } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { CopyButton } from '../../components/CopyButton';
import { isPlanError } from '../../components/plan/planErrors';
import { teamLimitFields } from '../../components/plan/planLabels';
import { Upsell, UpsellFromError } from '../../components/plan/Upsell';
import { usePlan } from '../../components/plan/usePlan';
import { getErrorMessage } from '../../lib/errors';
import { formatInstantDate } from '../../lib/format';
import type { BusinessRead, InviteCreated, MemberRead } from '../../types/api';
import { createInvite, removeMember, revokeInvite, useTeam } from './teamApi';

/** The accept link as something pasteable anywhere: absolute even if the API sent a path. */
function absoluteUrl(url: string): string {
  return url.startsWith('/') ? `${window.location.origin}${url}` : url;
}

interface TeamCardProps {
  business: BusinessRead;
}

/**
 * Who works on the active brand, and (for its owner) the way to add someone.
 *
 * Nothing is emailed by Autopostmate: the owner invites an address, copies the
 * accept link that comes back once, and sends it themselves. Seats are the
 * active members (owner included) plus the pending invites, against the
 * owner's plan; at the cap the form gives way to the upsell before the server
 * has to refuse. Members see the list only: inviting, removing, deleting the
 * brand and the plan are the owner's.
 */
export function TeamCard({ business }: TeamCardProps) {
  const isOwner = business.role === 'owner';
  const team = useTeam(business.id, isOwner);
  const { entitlements, limit: planLimit } = usePlan();

  const [email, setEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState<unknown>(null);
  const [created, setCreated] = useState<InviteCreated | null>(null);

  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [removing, setRemoving] = useState<MemberRead | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const activeCount = team.members.filter((member) => member.status === 'active').length;
  const seats = activeCount + team.invites.length;
  // The viewer's own plan is the brand's plan only when the viewer owns it; a
  // member is inside someone else's allowance, which the client cannot read.
  const limit = isOwner ? planLimit('team_members') : undefined;
  const atCap =
    isOwner &&
    !team.isLoading &&
    team.error === null &&
    entitlements !== null &&
    typeof limit === 'number' &&
    seats >= limit;

  async function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    setInviteError(null);
    setCreated(null);
    setIsInviting(true);
    try {
      const invite = await createInvite(business.id, address);
      setCreated(invite);
      setEmail('');
      team.reload();
    } catch (caught) {
      setInviteError(caught);
    } finally {
      setIsInviting(false);
    }
  }

  async function handleRevoke(inviteId: string) {
    setListError(null);
    setRevokingId(inviteId);
    try {
      await revokeInvite(business.id, inviteId);
      if (created?.id === inviteId) setCreated(null);
      team.reload();
    } catch (caught) {
      setListError(getErrorMessage(caught, 'Could not revoke the invite.'));
    } finally {
      setRevokingId(null);
    }
  }

  async function handleRemove() {
    if (!removing) return;
    setRemoveError(null);
    setIsRemoving(true);
    try {
      await removeMember(business.id, removing.user_id);
      setRemoving(null);
      team.reload();
    } catch (caught) {
      setRemoveError(getErrorMessage(caught, 'Could not remove them.'));
    } finally {
      setIsRemoving(false);
    }
  }

  let countLabel: string | null = null;
  if (!team.isLoading && team.error === null) {
    if (limit === null) countLabel = `${seats} · Unlimited`;
    else if (typeof limit === 'number') countLabel = `${seats} of ${limit}`;
    else countLabel = String(activeCount);
  }

  return (
    <section className="wizard settings-card team-card">
      <div className="settings-card__head">
        <h2 className="wizard__title">
          Team members
          {countLabel ? <span className="settings-card__count">{countLabel}</span> : null}
        </h2>
        <p className="settings-card__lede">
          {isOwner
            ? `People who work on ${business.name} with you: the brand profile, generating and reviewing posts. Inviting, removing, deleting the brand and the plan stay with you.`
            : `People who work on ${business.name}.`}
        </p>
      </div>

      {team.isLoading ? (
        <p className="field__hint" role="status">
          Loading
        </p>
      ) : team.error !== null ? (
        <p className="form__error" role="alert">
          {team.error}
        </p>
      ) : (
        <ul className="team-card__list" aria-label="Members">
          {team.members.map((member) => (
            <li className="team-card__row" key={member.user_id}>
              <div className="team-card__main">
                <span className="team-card__email">{member.email ?? 'Member'}</span>
                <span className="team-card__meta">
                  <span
                    className={
                      member.role === 'owner' ? 'badge badge--neutral' : 'badge badge--member'
                    }
                  >
                    {member.role === 'owner' ? 'Owner' : 'Member'}
                  </span>
                  {member.status === 'suspended' ? (
                    <span className="badge badge--locked">Suspended</span>
                  ) : null}
                  <span className="team-card__fact">
                    joined {formatInstantDate(member.joined_at)}
                  </span>
                </span>
              </div>
              {isOwner && member.role !== 'owner' ? (
                <button
                  className="button button--ghost button--danger-text"
                  type="button"
                  onClick={() => {
                    setRemoveError(null);
                    setRemoving(member);
                  }}
                >
                  Remove
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {isOwner && team.invites.length > 0 ? (
        <div className="team-card__section">
          <h3 className="team-card__subtitle">Pending invites</h3>
          <ul className="team-card__list" aria-label="Pending invites">
            {team.invites.map((invite) => (
              <li className="team-card__row team-card__row--pending" key={invite.id}>
                <div className="team-card__main">
                  <span className="team-card__email">{invite.email}</span>
                  <span className="team-card__meta">
                    <span className="badge badge--pending">Invited</span>
                    <span className="team-card__fact">
                      expires {formatInstantDate(invite.expires_at)}
                    </span>
                  </span>
                </div>
                <button
                  className="button button--ghost"
                  type="button"
                  disabled={revokingId === invite.id}
                  onClick={() => void handleRevoke(invite.id)}
                >
                  {revokingId === invite.id ? 'Revoking' : 'Revoke'}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {listError ? (
        <p className="form__error" role="alert">
          {listError}
        </p>
      ) : null}

      {created ? (
        <div className="team-card__link" role="status">
          <p className="team-card__link-lede">
            Invite for <strong>{created.email}</strong> is ready.
          </p>
          <div className="team-card__link-row">
            <input
              className="field__input team-card__link-input"
              type="text"
              readOnly
              aria-label="Accept link"
              value={absoluteUrl(created.accept_url)}
              onFocus={(event) => event.currentTarget.select()}
            />
            <CopyButton text={absoluteUrl(created.accept_url)} label="Copy link" />
          </div>
          <p className="field__hint">
            Send this link to them. It works for 7 days and only for that email.
          </p>
        </div>
      ) : null}

      <div className="settings-card__foot team-card__foot">
        {!isOwner ? (
          <p className="field__hint">Only the brand owner can invite.</p>
        ) : atCap && entitlements !== null && typeof limit === 'number' ? (
          <Upsell fields={teamLimitFields(entitlements, limit)} />
        ) : (
          <form
            className="team-card__form"
            onSubmit={(event) => void handleInvite(event)}
            noValidate
          >
            <label className="field team-card__field">
              <span className="field__label">Invite by email</span>
              <input
                className="field__input"
                type="email"
                name="invite-email"
                autoComplete="off"
                placeholder="name@company.com"
                required
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  setInviteError(null);
                }}
              />
            </label>
            <button
              className="button button--primary"
              type="submit"
              disabled={isInviting || email.trim() === ''}
            >
              {isInviting ? 'Inviting' : 'Invite'}
            </button>
            {inviteError ? (
              isPlanError(inviteError) ? (
                <UpsellFromError error={inviteError} className="team-card__error" />
              ) : (
                <p className="form__error team-card__error" role="alert">
                  {getErrorMessage(inviteError, 'Could not create the invite.')}
                </p>
              )
            ) : null}
          </form>
        )}
      </div>

      {removing ? (
        <ConfirmDialog
          title="Remove from the brand"
          message={`${removing.email ?? 'This member'} loses access to ${business.name} straight away. What they made stays. You can invite them again later.`}
          confirmLabel="Remove"
          busyLabel="Removing"
          isBusy={isRemoving}
          error={removeError}
          onConfirm={() => void handleRemove()}
          onDismiss={() => setRemoving(null)}
        />
      ) : null}
    </section>
  );
}
